import React, { useState, useEffect, FC, JSX } from 'react';
import api from '../../services/api';
import { BackendStatusProps } from '../../types/components';
import { ApiResponse } from '../../types/api';
import { AxiosInstance } from 'axios';

const BackendStatus: FC<BackendStatusProps> = (): JSX.Element | null => {
  const [status, setStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkBackendStatus();
  }, []);

  const checkBackendStatus = async (): Promise<void> => {
    try {
      setStatus('checking');
      setError(null);
      
      // Check if we're using mock API
      const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === 'true' && 
                           process.env.NODE_ENV === 'development';
      
      if (USE_MOCK_API) {
        setStatus('connected');
        return;
      }
      
      // Try to make a simple request to check if backend is running
      await (api as AxiosInstance).head('/');
      setStatus('connected');
    } catch (error: any) { // Explicitly type error as any
      console.error('Backend status check failed:', error);
      setStatus('disconnected');
      
      if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        setError('please try again after sometime');
      } else if (error.code === 'ECONNABORTED') {
        setError('please try again after sometime');
      } else {
        setError('please try again after sometime');
      }
    }
  };

  const getStatusColor = (): string => {
    switch (status) {
      case 'connected':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'disconnected':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  const getStatusIcon = (): JSX.Element => {
    switch (status) {
      case 'connected':
        return (
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'disconnected':
        return (
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
    }
  };

  // Check if we're using mock API
  const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === 'true' && 
                       process.env.NODE_ENV === 'development';
  
  if ((status === 'connected' || status==='checking') && !USE_MOCK_API) {
    return null; // Don't show anything if backend is connected
  }
  
  if (USE_MOCK_API && status === 'connected') {
    return (
      <div className="bg-primary-lightGreen/10 border border-primary-lightGreen/20 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-primary-lightGreen" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-primary-lightGreen">
              Development Mode
            </h3>
            <div className="mt-2 text-sm text-primary-lightGreen">
              <p>Using Mock API for development. All features are simulated.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`border rounded-lg p-4 mb-6 ${getStatusColor()}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getStatusIcon()}
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium">
            {status === 'checking' ? 'Loading Services' : 'Facing Connectivity Issue'}
          </h3>
          {error && (
            <div className="mt-2 text-sm">
              <p>{error}</p>
              <div className="mt-3">
                <button
                  onClick={checkBackendStatus}
                  className="text-sm font-medium underline hover:no-underline"
                >
                  Check Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackendStatus;
